/* eslint-disable @typescript-eslint/ban-ts-ignore */
/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import * as React from "react";
import { I18n } from "i18n-js";
import * as locales from "../../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import ScaleLoader from "react-spinners/ScaleLoader";
import { ErrorBoundary } from "react-error-boundary";
import { datadogRum } from "@datadog/browser-rum";

import * as API from "../../../api/billing";
import BillingInfoCard from "./components/billingInfoCard";
import CancelTrialCard from "./components/cancelTrialCard";
import ContactInfoCard from "./components/contactInfoCard";
import DeactivatedBanner from "./components/deactivatedBanner";
import ErrorFallbackCard from "../../reusable/errorFallbackCard";
import PurchaseOrderNumbersCard from "./components/purchaseOrderNumbersCard";
import RewardBillingCard from "./components/rewardBillingCard";
import SubscriptionBillingCard from "./components/subscriptionBillingCard";
import ToastMessage from "../../reusable/toastMessage";
import TrialBanner from "./components/trialBanner";
import setUpAxios from "../../../helperFunctions/helperFunctions";

import {
  ContainerData,
  ChargebeeInfoCardTypes,
  PortalSection,
  ToastMessageOptionTypes,
} from "./lib/types";
import {
  StyledContainer,
  StyledLinkSpan,
  StyledMasonry,
  StyledPageLoaderContainer,
  StyledPageHeader,
} from "./lib/styles";
import { defaultBillingPageData, defaultMessageOptions } from "./lib/constants";
import { initializeDatadogRUM } from "../../../utils";
import { theme } from "../../theme/theme";

declare global {
  interface Window {
    amplitude: any;
  }
}

const breakpointColumns = {
  default: 2,
  979: 1,
};

const BillingSettingsContainer = (): React.ReactElement => {
  const [data, setData] = React.useState<ContainerData>({});
  const [refetchData, setRefetchData] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingCard, setLoadingCard] = React.useState<ChargebeeInfoCardTypes>(
    ""
  );
  const [
    rewardBillingSuccessMessage,
    setRewardBillingSuccessMessage,
  ] = React.useState("");
  const [portal, setPortal] = React.useState();
  const [portalHasBeenOpened, setPortalHasBeenOpened] = React.useState<boolean>(
    false
  );
  const [
    toastMessageOptions,
    setToastMessageOptions,
  ] = React.useState<ToastMessageOptionTypes>(defaultMessageOptions);
  const [reactivateModal, setReactivateModal] = React.useState("");

  const updateToastMessage = (options: ToastMessageOptionTypes): void => {
    setToastMessageOptions(options);
  };

  const openPortal = (
    event: React.ChangeEvent,
    section: PortalSection,
    handleClose: () => void
  ): void => {
    const options = {
      close: (): void => {
        handleClose();
      },
      paymentSourceAdd: (): void => {
        window.amplitude?.track(
          "Submit Add a payment method ChargeBee Success"
        );
      },
    };
    // @ts-ignore
    portal.openSection(
      { sectionType: (window as any).Chargebee.getPortalSections()[section] },
      options
    );
    setPortalHasBeenOpened(true);
  };

  const fetchData = async (card?: ChargebeeInfoCardTypes): Promise<void> => {
    setLoading(true);
    setLoadingCard(card);

    try {
      const response = await API.getCompanyBillingPage();
      setData(response.data.data);
      const site = response.data.data.chargebeeSite;
      if (site) {
        const chargebeePortalResponse = await API.getCharbebeePortalSession();
        const instance = (window as any).Chargebee.init({ site });
        instance.setPortalSession(chargebeePortalResponse.data.portalSession);
        setPortal(instance.createChargebeePortal());
      } else {
        throw new Error("No site for Chargebee portal request");
      }
    } catch (error) {
      setData(defaultBillingPageData);
      datadogRum.addError(error);
    } finally {
      setLoading(false);
      setLoadingCard("");
    }
  };

  React.useEffect(() => {
    initializeDatadogRUM();
    // Call once in this container then all cards within this container
    // will have axios setup without needing to call it again
    setUpAxios();
    fetchData();
  }, []);

  /* istanbul ignore next - requires mocking closing chargbee portal then calling the callback */
  React.useEffect(() => {
    if (refetchData) {
      fetchData();
    }
  }, [refetchData]);

  React.useEffect(() => {
    if (portal && !portalHasBeenOpened) {
      const urlParams = new URLSearchParams(window.location.search);
      const openBillingModal = urlParams.get("open_payment_modal");
      if (openBillingModal === "true") {
        openPortal(null, "PAYMENT_SOURCES", () => setRefetchData(true));
      }
    }
  }, [portal]);

  return (
    <StyledContainer>
      <StyledPageHeader>
        {i18n.translate("title.plans_and_billing")}
      </StyledPageHeader>
      {loading ? (
        <StyledPageLoaderContainer data-testid="loader">
          <ScaleLoader
            color={theme.color.grayDark}
            loading={loading}
            height={45}
            width={6}
            margin={6}
          />
        </StyledPageLoaderContainer>
      ) : (
        <>
          {data.trialing && data.paymentMethodOnFile && (
            <TrialBanner trialEndDate={data.trialEndDate} />
          )}
          {data.subscriptionStatus === "cancelled" && (
            <DeactivatedBanner
              cardType={data.chargebeeInfo.card.type}
              companyBlocklisted={data.companyBlocklisted}
              companyId={data.companyId}
              hasPaymentMethod={data.paymentMethodOnFile}
              last4={data.chargebeeInfo.card.last4}
              openPortal={openPortal}
              setRefetchData={setRefetchData}
              subscriptionType={data.subscriptionType}
              updateToastMessage={updateToastMessage}
              userCount={data.userCount}
              modal={reactivateModal}
              setModal={setReactivateModal}
            />
          )}
          <StyledMasonry
            breakpointCols={breakpointColumns}
            className="billing-settings-grid"
            columnClassName="grid-column"
            data-testid="billing-settings"
          >
            <ErrorBoundary FallbackComponent={ErrorFallbackCard}>
              <SubscriptionBillingCard
                billingType={data.billingType}
                card={data.chargebeeInfo.card}
                openPortal={openPortal}
                paymentDueOn={data.paymentDueOn}
                paymentMethodOnFile={data.paymentMethodOnFile}
                refetchData={refetchData}
                refillAmount={data.refillAmount}
                refillThreshold={data.refillThreshold}
                setPageLoading={setLoading}
                setRefetchData={setRefetchData}
                showSuperAdminFeature={data.showSuperAdminFeature}
                subscriptionAutoCollection={data.subscriptionAutoCollection}
                subscriptionId={data.subscriptionId}
                subscriptionType={data.subscriptionType}
                updateToastMessage={updateToastMessage}
                openReactivate={() => setReactivateModal("reactivate")}
              />
            </ErrorBoundary>
            <>
              <ErrorBoundary FallbackComponent={ErrorFallbackCard}>
                <ContactInfoCard
                  companyName={data.chargebeeInfo.companyName}
                  email={data.chargebeeInfo.email}
                  fetchData={fetchData}
                  loading={loadingCard === "contact"}
                  openPortal={openPortal}
                  phone={data.chargebeeInfo.phone}
                />
              </ErrorBoundary>
              <ErrorBoundary FallbackComponent={ErrorFallbackCard}>
                <BillingInfoCard
                  billingAddress={data.chargebeeInfo.billingAddress}
                  paymentMethodOnFile={data.paymentMethodOnFile}
                  fetchData={fetchData}
                  loading={loadingCard === "billing"}
                  openPortal={openPortal}
                />
              </ErrorBoundary>
              {data.paymentMethodOnFile && (
                <ErrorBoundary FallbackComponent={ErrorFallbackCard}>
                  <PurchaseOrderNumbersCard
                    companyId={data.companyId}
                    updateToastMessage={updateToastMessage}
                  />
                </ErrorBoundary>
              )}
            </>
            <>
              <ErrorBoundary FallbackComponent={ErrorFallbackCard}>
                <RewardBillingCard
                  billingType={data.billingType}
                  card={data.chargebeeInfo.card}
                  fetchPaymentMethodData={fetchData}
                  openPortal={openPortal}
                  paymentDueOn={data.paymentDueOn}
                  refetchData={refetchData}
                  refillAmount={data.refillAmount}
                  refillThreshold={data.refillThreshold}
                  setRefetchData={setRefetchData}
                  setSuccessMessage={setRewardBillingSuccessMessage}
                  showSuperAdminFeature={data.showSuperAdminFeature}
                  subscriptionAutoCollection={data.subscriptionAutoCollection}
                  successMessage={rewardBillingSuccessMessage}
                  updateToastMessage={updateToastMessage}
                />
              </ErrorBoundary>
              {data.trialing && data.subscriptionStatus !== "cancelled" && (
                <CancelTrialCard updateToastMessage={updateToastMessage} />
              )}
            </>
          </StyledMasonry>

          <StyledLinkSpan style={{ fontWeight: 600 }}>
            {i18n.translate("admin_pages.settings.for_support")}{" "}
            <a
              href="https://bonusly.zoom.us/webinar/register/WN_-GQuC-XGQ6iL1aySjLdnZA"
              rel="noreferrer"
              target="_blank"
            >
              {i18n.translate("admin_pages.settings.join_office_hours")}
            </a>{" "}
            {i18n.translate("admin_pages.settings.or")}{" "}
            <a href="http://help.bonus.ly" rel="noreferrer" target="_blank">
              {i18n.translate("admin_pages.settings.visit_help_center")}
            </a>
          </StyledLinkSpan>
        </>
      )}
      <ToastMessage
        isOpen={toastMessageOptions.isOpen}
        messageType={toastMessageOptions.type}
        closeToastMessage={(): void => {
          updateToastMessage({
            ...toastMessageOptions,
            isOpen: false,
          });
        }}
      >
        <span>{toastMessageOptions.message}</span>
      </ToastMessage>
    </StyledContainer>
  );
};

export default BillingSettingsContainer;
